/**
 * Counter Cultures - "Mi Proyecto" quote-list.
 *
 * A lightweight, native alternative to a wishlist/quote-request app: visitors
 * add pieces to a running list (stored in localStorage, no account required),
 * see a cart-style running subtotal, fill in required contact details, and
 * send the request either by email (Shopify's native `{% form 'contact' %}`,
 * delivered to the store's contact email) or WhatsApp.
 *
 * Intentionally framework-free vanilla JS so it has no dependency on the
 * theme's internal component bundle beyond the existing <theme-drawer> element.
 */

const STORAGE_KEY = 'cc_project_items';
const WHATSAPP_NUMBER = '524151548375';

const currencyFmt = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
});

function getItems() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function saveItems(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  renderAll();
}

function addItem(item) {
  const items = getItems();
  if (items.some((i) => i.id === item.id)) return;
  items.push(item);
  saveItems(items);
}

function removeItem(id) {
  saveItems(getItems().filter((i) => i.id !== id));
}

function clearItems() {
  saveItems([]);
}

/** Parse a display price string like "$12,345.00 MXN" into a number, or null. */
function parsePrice(str) {
  if (!str) return null;
  const cleaned = String(str).replace(/[^0-9.,]/g, '');
  if (!cleaned) return null;
  // Drop thousands separators (commas), keep the decimal point.
  const normalized = cleaned.replace(/,/g, '');
  const value = parseFloat(normalized);
  return Number.isFinite(value) ? value : null;
}

/** { total, missingCount } across the current items. */
function computeTotal(items) {
  let total = 0;
  let missingCount = 0;
  items.forEach((item) => {
    const value = parsePrice(item.price);
    if (value === null) {
      missingCount += 1;
    } else {
      total += value;
    }
  });
  return { total, missingCount };
}

/** Human-readable, multi-line itemization used in both email and WhatsApp. */
function buildItemLines(items) {
  const { total } = computeTotal(items);
  const groups = groupByRoom(items);
  const lines = [];
  // Grouped in the email and the WhatsApp message too - the grouping is only useful if
  // it survives to the person writing the quote.
  groups.forEach(([room, group]) => {
    if (groups.length > 1) {
      lines.push(`— ${room} —`);
    }
    group.forEach((i) => {
      lines.push(`• ${i.title}${i.price ? ` — ${i.price}` : ''} (${i.url})`);
    });
    if (groups.length > 1) {
      lines.push(`  Subtotal ${room}: ${currencyFmt.format(computeTotal(group).total)}`);
      lines.push('');
    }
  });
  lines.push('');
  lines.push(`Subtotal estimado: ${currencyFmt.format(total)}`);
  return lines.join('\n');
}

function getFormFields() {
  const form = document.getElementById('project-contact-form');
  if (!form) return null;
  return {
    form,
    projectName: (form.querySelector('[name="contact[Nombre del Proyecto]"]') || {}).value || '',
    name: (form.querySelector('[name="contact[name]"]') || {}).value || '',
    email: (form.querySelector('[name="contact[email]"]') || {}).value || '',
    phone: (form.querySelector('[name="contact[phone]"]') || {}).value || '',
    address: (form.querySelector('[name="contact[Dirección]"]') || {}).value || '',
    comments: (form.querySelector('[name="contact[body]"]') || {}).value || '',
  };
}

function buildWhatsAppUrl(items, fields) {
  const lines = [
    'Hola, me gustaría solicitar una cotización para mi proyecto:',
    '',
  ];
  if (fields.projectName) lines.push(`Proyecto: ${fields.projectName}`, '');
  lines.push(
    buildItemLines(items),
    '',
    '— Mis datos —',
    `Nombre: ${fields.name}`,
    `Correo: ${fields.email}`,
  );
  if (fields.phone) lines.push(`Teléfono: ${fields.phone}`);
  lines.push(`Dirección: ${fields.address}`);
  if (fields.comments) lines.push(`Comentarios: ${fields.comments}`);
  // Lead reference, set by snippets/cc-ads-conversions.liquid on this same click (capture phase, runs first).
  if (window.__ccWaRef) lines.push('', `Código de referencia: ${window.__ccWaRef}`);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join('\n'))}`;
}

function updateCountBubbles(count) {
  document.querySelectorAll('[data-project-count]').forEach((el) => {
    el.textContent = count > 0 ? String(count) : '';
    el.classList.toggle('project-bubble--empty', count === 0);
  });
}

/**
 * The button's data-* attributes are stamped at render time, so on a product with a
 * variant picker they still describe the FIRST variant after the shopper switches
 * finish. Read the form's current variant id instead and look it up in the map the
 * snippet embedded. Also fixes a latent bug: the drawer subtotal used to take the
 * first variant's price regardless of what was selected.
 */
function readSelectedVariant(button) {
  let finish = button.getAttribute('data-project-finish') || '';
  let price = button.getAttribute('data-project-price') || '';
  const raw = button.getAttribute('data-project-variants');
  if (!raw) return { finish, price };
  try {
    const map = JSON.parse(raw);
    const input = document.querySelector(
      'form[data-type="add-to-cart-form"] input[name="id"]'
    );
    const entry = input && map[input.value];
    if (entry) {
      finish = entry[0] || finish;
      price = entry[1] || price;
    }
  } catch (e) {
    /* malformed map - fall back to the product-level attributes */
  }
  return { finish, price };
}

/* ---------------------------------------------------------------------------
 * #4 Rooms
 *
 * What makes this a project tool rather than a wishlist: a cart cannot express
 * "these four go in the guest bath and those two go in the kitchen", and that is
 * exactly how the customer thinks and how Counter Cultures has to quote it.
 *
 * A fixed list, not free text. Free text fragments instantly ("Bano principal",
 * "bano ppal", "Ba~no 1") and grouping on fragments is worthless. "Otra area" is the
 * escape hatch for anything unusual.
 * ------------------------------------------------------------------------- */
const ROOMS = [
  'Baño principal',
  'Baño de visitas',
  'Baño secundario',
  'Cocina',
  'Barra / cocina secundaria',
  'Lavandería',
  'Exterior / jardín',
  'Otra área',
];
const NO_ROOM = 'Sin asignar';

/** Items grouped by room, in ROOMS order, with unassigned last. */
function groupByRoom(items) {
  const groups = new Map();
  items.forEach((item) => {
    const room = item.room || NO_ROOM;
    if (!groups.has(room)) groups.set(room, []);
    groups.get(room).push(item);
  });
  const ordered = [];
  ROOMS.forEach((r) => {
    if (groups.has(r)) ordered.push([r, groups.get(r)]);
  });
  groups.forEach((v, k) => {
    if (!ROOMS.includes(k) && k !== NO_ROOM) ordered.push([k, v]);
  });
  if (groups.has(NO_ROOM)) ordered.push([NO_ROOM, groups.get(NO_ROOM)]);
  return ordered;
}

function setRoom(id, room) {
  const items = getItems();
  const item = items.find((i) => String(i.id) === String(id));
  if (!item) return;
  item.room = room;
  saveItems(items);
}

function roomSelect(item) {
  const current = item.room || '';
  const options = ['<option value="">' + NO_ROOM + '</option>']
    .concat(
      ROOMS.map(
        (r) =>
          '<option value="' + r + '"' + (r === current ? ' selected' : '') + '>' + r + '</option>'
      )
    )
    .join('');
  return (
    '<select class="project-drawer__room" data-project-room="' +
    item.id +
    '" aria-label="Área para ' +
    String(item.title).replace(/"/g, '&quot;') +
    '">' +
    options +
    '</select>'
  );
}

/* ---------------------------------------------------------------------------
 * #6 Sharing
 *
 * These decisions get made by more than one person - the owner, the architect, the
 * contractor - so a project that only exists in one browser's localStorage dies there.
 *
 * The link carries only { handle, room, finish }, not titles or prices: the receiving
 * browser re-fetches each product, so a shared link never shows a stale price, and the
 * URL stays short enough to paste into WhatsApp.
 * ------------------------------------------------------------------------- */
function b64encode(str) {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64decode(str) {
  const padded = str.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded);
  return new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)));
}

/** Legacy items were saved before handles were stored; recover it from the URL. */
function itemHandle(item) {
  if (item.handle) return item.handle;
  const match = String(item.url || '').match(/\/products\/([^/?#]+)/);
  return match ? match[1] : '';
}

function buildShareUrl(items) {
  const payload = items
    .map((i) => ({ h: itemHandle(i), r: i.room || '', f: i.finish || '' }))
    .filter((e) => e.h);
  if (!payload.length) return '';
  return (
    window.location.origin + '/pages/catalogo#proyecto=' + b64encode(JSON.stringify(payload))
  );
}

/** Rebuild a shared list from the URL fragment. Merges, never clobbers. */
async function importFromHash() {
  const match = window.location.hash.match(/^#proyecto=(.+)$/);
  if (!match) return false;

  let payload;
  try {
    payload = JSON.parse(b64decode(match[1]));
  } catch (e) {
    return false;
  }
  if (!Array.isArray(payload)) return false;

  const items = getItems();
  const known = new Set(items.map((i) => String(i.id)));
  let added = 0;

  for (const entry of payload) {
    if (!entry || !entry.h) continue;
    try {
      const product = await fetch('/products/' + entry.h + '.js').then((r) =>
        r.ok ? r.json() : null
      );
      if (!product || known.has(String(product.id))) continue;
      known.add(String(product.id));
      const variant = product.variants && product.variants[0];
      items.push({
        id: String(product.id),
        handle: entry.h,
        title: product.title,
        image: product.featured_image || '',
        url: window.location.origin + '/products/' + entry.h,
        // A quote-only product reports price 0. Leaving it blank routes it into the
        // "sin precio en línea — los cotizaremos aparte" count instead of displaying
        // a $0.00 line item, which reads as broken.
        price: variant && variant.price > 0 ? currencyFmt.format(variant.price / 100) : '',
        tono: '',
        finish: entry.f || '',
        room: entry.r || '',
      });
      added += 1;
    } catch (e) {
      /* a product that has since been removed is skipped, not fatal */
    }
  }

  if (added) saveItems(items);
  history.replaceState(null, '', window.location.pathname + window.location.search);
  return added > 0;
}

/* ---------------------------------------------------------------------------
 * #7 Welcome back
 *
 * A bathroom takes weeks to specify. The site used to treat every visit as the first,
 * so a half-built project was invisible unless you happened to notice the header count.
 * Shown once per session, and never on top of an already-open drawer.
 * ------------------------------------------------------------------------- */
function showWelcomeBack(count) {
  if (document.getElementById('project-welcome')) return;
  const bar = document.createElement('div');
  bar.id = 'project-welcome';
  bar.className = 'project-welcome';
  bar.setAttribute('role', 'status');
  bar.innerHTML =
    '<span>Tienes <strong>' +
    count +
    '</strong> pieza' +
    (count === 1 ? '' : 's') +
    ' guardada' +
    (count === 1 ? '' : 's') +
    ' en tu proyecto.</span>' +
    '<button type="button" data-project-welcome-open>Ver proyecto</button>' +
    '<button type="button" class="project-welcome__close" data-project-welcome-close ' +
    'aria-label="Cerrar">&times;</button>';
  document.body.appendChild(bar);
  requestAnimationFrame(() => bar.classList.add('is-in'));
}

function dismissWelcomeBack() {
  try {
    sessionStorage.setItem('cc_project_welcomed', '1');
  } catch (e) {
    /* private mode - it will simply show again next load */
  }
  const bar = document.getElementById('project-welcome');
  if (bar) bar.remove();
}


/**
 * Normalise a finish name to one of the store's `Tono_` families.
 *
 * Needed because the raw Acabado string is written differently by every brand -
 * "Chrome", "Cromo", "Polished Chrome", "Cromo Pulido" are one colour - and comparing
 * those strings directly would report a mismatch on a project that is perfectly matched.
 *
 * Returns null when nothing matches. An unrecognised finish is then IGNORED rather than
 * counted as its own family: a false "you have 4 finishes" warning trains people to
 * dismiss the notice, which costs more than a missed one.
 *
 * Order matters - the specific patterns must be tested before the general ones
 * ("acero inoxidable" before "acero", "dorado" before the \boro\b word match).
 */
const TONE_FAMILIES = [
  [/acero\s*inox|stainless|\binox\b/, 'Acero Inoxidable'],
  [/dos\s*tonos|two[\s-]*tone|bicolor/, 'Dos Tonos'],
  [/cromo|chrome/, 'Cromo'],
  [/n[ií]quel|nickel/, 'N\u00edquel'],
  [/bronce|bronze|oil[\s-]*rubbed/, 'Bronce'],
  [/cobre|copper/, 'Cobre'],
  [/lat[oó]n|brass|dorado|\bgold\b|\boro\b|champ[aá]n|champagne/, 'Dorado y Lat\u00f3n'],
  [/negro|\bblack\b/, 'Negro'],
  [/blanco|\bwhite\b/, 'Blanco'],
  [/\bgris\b|\bgrey\b|\bgray\b|grafito|gunmetal|antracita|pewter/, 'Gris'],
];

function toneFamily(finish) {
  if (!finish) return null;
  // Strip accents so "níquel" and "niquel" both match.
  const t = finish
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  for (const [pattern, family] of TONE_FAMILIES) {
    if (pattern.test(t)) return family;
  }
  return null;
}

/**
 * Mixed-finish warning. Counter Cultures already does this by phone - someone orders a
 * shower in matte black and a faucet in chrome and gets a call before it goes to the
 * factory. This surfaces it while they are still building the list.
 *
 * Grouped on the `Tono_` family, not the raw finish name, because the same colour is
 * spelled differently per brand ("Chrome" / "Cromo Pulido" / "Polished Chrome") and the
 * Tono taxonomy exists precisely to normalise that into ~10 families.
 *
 * Phrased as a question, never an error: mixing finishes on purpose is a legitimate and
 * common design choice, and some products carry a default tone tag rather than a real one.
 */
function renderFinishCheck(items) {
  const box = document.getElementById('project-drawer-finishcheck');
  if (!box) return;

  const groups = new Map();
  items.forEach((item) => {
    const key = toneFamily(item.finish) || item.tono || '';
    if (!key) return;
    if (!groups.has(key)) groups.set(key, 0);
    groups.set(key, groups.get(key) + 1);
  });

  if (groups.size < 2) {
    box.hidden = true;
    box.innerHTML = '';
    return;
  }

  const names = [...groups.keys()];
  const list = names.slice(0, 4).join(', ') + (names.length > 4 ? '\u2026' : '');
  box.innerHTML =
    '<p class="project-drawer__finishcheck-title">Tu proyecto tiene ' +
    names.length +
    ' acabados distintos</p>' +
    '<p class="project-drawer__finishcheck-body">' +
    list +
    '. Si es a prop\u00f3sito, perfecto \u2014 combinar acabados se ve muy bien cuando es ' +
    'intencional. Si no lo era, escr\u00edbenos y lo revisamos contigo antes de que lo mandes.</p>';
  box.hidden = false;
}

function renderDrawerItems(items, justPosted) {
  const list = document.getElementById('project-drawer-items');
  const empty = document.getElementById('project-drawer-empty');
  const summary = document.getElementById('project-drawer-summary');
  const form = document.getElementById('project-contact-form');
  const clearBtn = document.getElementById('project-drawer-clear');
  if (!list || !empty) return;

  const hasItems = items.length > 0;

  // The form stays visible after a successful post so the "thank you" shows.
  empty.hidden = hasItems || justPosted;
  if (summary) summary.hidden = !hasItems;
  if (form) form.hidden = !hasItems && !justPosted;
  if (clearBtn) clearBtn.hidden = !hasItems;
  const shareRow = document.getElementById('project-drawer-share-row');
  if (shareRow) shareRow.hidden = !hasItems;

  renderFinishCheck(items);

  if (!hasItems) {
    list.innerHTML = '';
    return;
  }

  list.innerHTML = groupByRoom(items)
    .map(([room, group]) => {
      const { total } = computeTotal(group);
      const rows = group
        .map(
          (item) => `
        <div class="project-drawer__item" data-project-item="${item.id}">
          ${
            item.image
              ? `<img src="${item.image}" alt="" width="64" height="64" class="project-drawer__item-image">`
              : '<div class="project-drawer__item-image project-drawer__item-image--placeholder"></div>'
          }
          <div class="project-drawer__item-info">
            <a href="${item.url}" class="project-drawer__item-title">${item.title}</a>
            ${item.price ? `<span class="project-drawer__item-price">${item.price}</span>` : ''}
            ${roomSelect(item)}
          </div>
          <button type="button" class="project-drawer__item-remove" data-project-remove="${item.id}" aria-label="Quitar">
            &times;
          </button>
        </div>
      `
        )
        .join('');
      // The heading is suppressed until the project actually spans more than one area -
      // a single-room project does not need to be told it has one room.
      const heading =
        groupByRoom(items).length > 1
          ? `<div class="project-drawer__roomhead">
               <span>${room}</span>
               <span class="project-drawer__roomtotal">${currencyFmt.format(total)}</span>
             </div>`
          : '';
      return `<div class="project-drawer__roomgroup">${heading}${rows}</div>`;
    })
    .join('');

  // Running total (#3)
  const { total, missingCount } = computeTotal(items);
  const totalEl = document.getElementById('project-drawer-total');
  const noteEl = document.getElementById('project-drawer-summary-note');
  if (totalEl) totalEl.textContent = currencyFmt.format(total);
  if (noteEl && missingCount > 0) {
    noteEl.textContent = `Precios de referencia. ${missingCount} artículo(s) sin precio en línea — los cotizaremos aparte.`;
  }
}

/** Paint the added/not-added state on every tile button currently in the DOM. */
function syncCardButtons(items) {
  const ids = new Set(items.map((i) => String(i.id)));
  document.querySelectorAll('[data-project-quiet]').forEach((button) => {
    const on = ids.has(String(button.getAttribute('data-project-add')));
    button.setAttribute('aria-pressed', String(on));
    button.setAttribute(
      'title',
      on ? 'Quitar de Mi Proyecto' : 'A\u00f1adir a Mi Proyecto'
    );
  });
}

function renderAll(justPosted) {
  const items = getItems();
  updateCountBubbles(items.length);
  syncCardButtons(items);
  renderDrawerItems(items, justPosted);
}

function openProjectDrawer() {
  const drawer = document.getElementById('project-drawer');
  if (drawer && typeof drawer.toggle === 'function') {
    if (!drawer.hasAttribute('open')) drawer.toggle();
  }
}

/** Validate the required contact fields; returns true if OK. */
function validateContactForm() {
  const form = document.getElementById('project-contact-form');
  if (!form) return false;
  if (typeof form.reportValidity === 'function') {
    return form.reportValidity();
  }
  return true;
}

document.addEventListener('DOMContentLoaded', () => {
  // If the PROJECT contact form just posted, Shopify reloads the page with the
  // submitted form's id as the URL fragment (#project-contact-form). We key off
  // that fragment ONLY — not the generic `?contact_posted=true` param nor the
  // `[data-project-posted]` marker, both of which also appear after the wholesale
  // or appointment forms post and would otherwise hijack those pages' own
  // confirmations and wipe the saved project list.
  const posted = window.location.hash === '#project-contact-form';

  renderAll(posted);

  // A shared link arrives as #proyecto=...; import it, then re-render.
  importFromHash().then((imported) => {
    if (imported) {
      renderAll(false);
      openProjectDrawer();
      return;
    }
    let welcomed = false;
    try {
      welcomed = sessionStorage.getItem('cc_project_welcomed') === '1';
    } catch (e) {
      welcomed = false;
    }
    const count = getItems().length;
    const drawer = document.getElementById('project-drawer');
    const drawerOpen = drawer && drawer.hasAttribute('open');
    if (!welcomed && !posted && !drawerOpen && count > 0) {
      showWelcomeBack(count);
    }
  });

  if (posted) {
    openProjectDrawer();
    // Clear the saved project now that it's been submitted.
    localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
    updateCountBubbles(0);
  }

  // Collection grids swap their tiles in on filter and pagination without a page load,
  // so freshly inserted buttons would otherwise render in the wrong state.
  let repaintQueued = false;
  new MutationObserver(() => {
    if (repaintQueued) return;
    repaintQueued = true;
    requestAnimationFrame(() => {
      repaintQueued = false;
      syncCardButtons(getItems());
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-project-room]');
    if (!select) return;
    setRoom(select.getAttribute('data-project-room'), select.value);
  });

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-project-add]');
    if (addButton) {
      event.preventDefault();
      // A tile button sits inside <product-card on:click="/handleViewTransition">, which
      // would navigate to the product the instant you add it.
      event.stopPropagation();

      const id = addButton.getAttribute('data-project-add');
      const quiet = addButton.hasAttribute('data-project-quiet');

      // Tile buttons toggle, so a mis-click is undone in place rather than by opening
      // the drawer to remove it.
      if (quiet && getItems().some((i) => i.id === id)) {
        removeItem(id);
        return;
      }

      const live = readSelectedVariant(addButton);
      addItem({
        id: id,
        handle: addButton.getAttribute('data-project-handle') || '',
        title: addButton.getAttribute('data-project-title') || '',
        image: addButton.getAttribute('data-project-image') || '',
        url: addButton.getAttribute('data-project-url') || '',
        price: live.price,
        tono: addButton.getAttribute('data-project-tono') || '',
        finish: live.finish,
      });
      // Adding from a grid means adding several in a row; a drawer over the grid after
      // each one is exactly the friction this feature exists to remove.
      if (!quiet) openProjectDrawer();
      return;
    }

    if (event.target.closest('[data-project-welcome-open]')) {
      dismissWelcomeBack();
      openProjectDrawer();
      return;
    }

    if (event.target.closest('[data-project-welcome-close]')) {
      dismissWelcomeBack();
      return;
    }

    const shareButton = event.target.closest('#project-drawer-share');
    if (shareButton) {
      event.preventDefault();
      const url = buildShareUrl(getItems());
      if (!url) return;

      // The link is ALWAYS revealed in a selectable field, and the clipboard write is
      // only a convenience on top. navigator.clipboard is denied often enough - insecure
      // context, permissions policy, in-app browsers - that treating it as the delivery
      // mechanism leaves people with a button that says "copied" and an empty clipboard,
      // or nothing at all. A visible URL cannot fail.
      const field = document.getElementById('project-drawer-share-url');
      const note = document.getElementById('project-drawer-share-note');
      if (field) {
        field.value = url;
        field.hidden = false;
        field.focus();
        field.select();
      }
      const say = (message) => {
        if (note) {
          note.textContent = message;
          note.hidden = false;
        }
      };
      say('Selecciona y copia el enlace, o mándalo por WhatsApp.');
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url).then(
          () => say('¡Enlace copiado! Ya lo puedes pegar donde quieras.'),
          () => {
            /* keep the manual instruction that is already showing */
          }
        );
      }
      return;
    }

    const shareWhats = event.target.closest('#project-drawer-share-whatsapp');
    if (shareWhats) {
      event.preventDefault();
      const url = buildShareUrl(getItems());
      if (!url) return;
      window.open(
        'https://wa.me/?text=' +
          encodeURIComponent('Mira el proyecto que armé en Counter Cultures: ' + url),
        '_blank',
        'noopener'
      );
      return;
    }

    const removeButton = event.target.closest('[data-project-remove]');
    if (removeButton) {
      event.preventDefault();
      removeItem(removeButton.getAttribute('data-project-remove'));
      return;
    }

    const clearButton = event.target.closest('#project-drawer-clear');
    if (clearButton) {
      event.preventDefault();
      clearItems();
      return;
    }

    // WhatsApp path (#4): require the contact fields, then open wa.me.
    const whatsappBtn = event.target.closest('#project-drawer-whatsapp-btn');
    if (whatsappBtn) {
      event.preventDefault();
      const items = getItems();
      if (!items.length) return;
      if (!validateContactForm()) return;
      const fields = getFormFields();
      window.open(buildWhatsAppUrl(items, fields), '_blank', 'noopener');
      return;
    }

    const opener = event.target.closest('a[href="#project-drawer"]');
    if (opener) {
      event.preventDefault();
      openProjectDrawer();
    }
  });

  // Email path (#4): inject the itemized list + total into the hidden field
  // right before the native contact form submits.
  const contactForm = document.getElementById('project-contact-form');
  if (contactForm) {
    contactForm.addEventListener('submit', () => {
      const listField = document.getElementById('project-drawer-list-field');
      if (listField) listField.value = buildItemLines(getItems());
    });
  }

  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target && event.target.id === 'project-drawer') {
      renderAll();
    }
  });
});
